import { Injector } from '@angular/core';
import { DateParserService } from '../../services/daily-log/daily-log-time/date-parser-service';
import {
  DateLostInTimeResult,
  MultipleTimeZoneResult,
} from '../time-zones/time-zone.models';
import { TimeZoneService } from '../../services/time-zone/time-zone.service';
import { cloneDeep } from 'lodash';
import { DateTime, DurationObject, Settings } from 'luxon';
import { DateService } from '../../services/date/date.service';

export class PtLuxonDate {
  public static readonly millisecondsInDay = 86400000;
  private readonly millisecondsInHour = 3600000;
  private readonly millisecondsInMinute = 60000;

  public dateTime: DateTime;
  public isLostInTime: DateLostInTimeResult = {result: false, timeZone: null};
  public multipleOffsets: MultipleTimeZoneResult;

  private dateParserService: DateParserService;

  constructor(_dateTime?: DateTime, setNull?: boolean, useLocalOffset?: boolean) {
    this.setDateParserService();

    this.valueOf = function () {
      return this.dateTime;
    };

    if (useLocalOffset) {
      // we may need to use local offset e.g when setting up the app for the first time, so set the default zone to local here, and set it back later to utc
      Settings.defaultZone = 'local';
    }

    if (!!_dateTime && isFinite(_dateTime.toJSDate().getTime())) {
      this.dateTime = _dateTime;
    } else {
      if (setNull) {
        this.dateTime = null;
      } else {
        this.dateTime = DateTime.local().toLocal();
      }
    }

    this.isLostInTime = {result: false, timeZone: null};

    Settings.defaultZone = 'utc';
  }

  public offsetNull() {
    return this.multipleOffsets && this.multipleOffsets.result && this.multipleOffsets.selectedOffsetMinutes == null;
  }

  public offsetNotNull() {
    return !this.multipleOffsets || !this.multipleOffsets.result || this.multipleOffsets.result && this.multipleOffsets.selectedOffsetMinutes != null;
  }

  getDate(): DateTime {
    return this.dateTime;
  }

  static assignPtLuxonDate(ptLuxonDate: PtLuxonDate): PtLuxonDate {
    if (!ptLuxonDate) {
      ptLuxonDate = new PtLuxonDate();
      ptLuxonDate.dateTime = null;

      return ptLuxonDate;
    }

    if (typeof (ptLuxonDate) !== 'object') {
      throw Error('Not an object.');
    }

    // a PtLuxonDate returned from the API will have the DateTime as an ISO string, so we need to create a luxon object from it
    if (typeof ptLuxonDate.dateTime === 'string') {
      ptLuxonDate.dateTime = DateTime.fromISO(ptLuxonDate.dateTime);
    }

    ptLuxonDate = Object.assign(new PtLuxonDate(), ptLuxonDate);

    return ptLuxonDate;
  }

  setDate(_dateTime: DateTime) {
    this.dateTime = cloneDeep(_dateTime);
  }

  async addDurationToDateTime(duration: DurationObject, dateService: DateService, timeZoneService: TimeZoneService, keepLocalTime: boolean) {
    this.dateTime = await dateService.addDuration(this.dateTime, duration, keepLocalTime);
    await this.checkForMultipleOffsets(timeZoneService, this.dateTime ? this.dateTime.offset / 60 : null);
    await this.checkForDateLostInTime(timeZoneService);
  }

  public setOffset(secondsOffset: number, keepLocalTime: boolean = true) {
    let offsetString = PtLuxonDate.getOffsetString(secondsOffset);

    this.dateTime = this.dateTime.setZone(offsetString, {keepLocalTime: keepLocalTime}); // without keepLocalTime: true, time will change when applying offset change

    if (this.multipleOffsets) {
      this.multipleOffsets.selectedOffsetMinutes = this.dateTime.offset;
    }
  }

  public async checkForDateLostInTime(timeZoneService: TimeZoneService) {
    this.isLostInTime = await timeZoneService.isDateLostInTime(this.dateTime);
  }

  public async checkForMultipleOffsets(timeZoneService: TimeZoneService, savedOffsetHours?: number) {
    this.multipleOffsets = await timeZoneService.checkForNegativeTimeZone(this.dateTime);

    if (this.multipleOffsets && this.multipleOffsets.result && savedOffsetHours != null) {
      this.setOffset(savedOffsetHours * 3600);
    }
  }

  public static getOffsetString(secondsOffset: number) {
    let offsetString = '';

    if (secondsOffset > 0) {
      offsetString = 'utc+' + (secondsOffset / 3600);
    } else if (secondsOffset < 0) {
      offsetString = 'utc' + (secondsOffset / 3600);
    } else if (secondsOffset == 0) {
      offsetString = 'utc';
    }

    return offsetString;
  }

  public setDateOnly(year: number, month: number, day: number) {
    this.dateTime = this.dateTime.set({
      year: year,
      month: month,
      day: day,
      hour: this.dateTime.hour,
      minute: this.dateTime.minute
    });
  }

  public setTimeOnly(hours: number, minutes: number, seconds?: number, milliseconds?: number) {
    this.dateTime = this.dateTime.set({
      year: this.dateTime.year,
      month: this.dateTime.month,
      day: this.dateTime.day,
      hour: hours,
      minute: minutes,
      second: seconds ? seconds : 0,
      millisecond: milliseconds ? milliseconds : 0
    });
  }

  // determines the correct day that a date falls by adding the in-app timezone
  async setDay(amount: number, timeZoneService?: TimeZoneService, baseDay?: PtLuxonDate, noNegative?: boolean, minimumDate?: DateTime) {
    let difference = 0;

    if (baseDay.dateTime && this.dateTime) {
      const currentDayCount = await this.getDay();
      const baseDayCount = await baseDay.getDay();

      difference = amount - (currentDayCount - baseDayCount);
    }

    if (this.dateTime != null) {
      this.dateTime = this.dateTime.plus({millisecond: (PtLuxonDate.millisecondsInDay * difference)});

      if (minimumDate && this.dateTime < minimumDate) {
        this.dateTime.set({day: minimumDate.day});
      }
    }
  }

  public getDay(): number {
    if (!this.dateTime) {
      return -1;
    }

    const leapYearsAdjustment = Math.floor(this.dateTime.year / 4);
    const yearDays = this.dateTime.year * 365;
    const daysInYear = this.getDaysSinceMonth(this.dateTime.month - 1);
    const days = this.dateTime.day;

    return yearDays + daysInYear + days + leapYearsAdjustment;
  }

  private getDaysSinceMonth(month: number): number {
    let dayCount = 0;
    month--;

    while (month >= 0) {
      switch (month) {
        case 0:
          dayCount += 31;
          break;
        case 1:
          dayCount += 28;
          break;
        case 2:
          dayCount += 31;
          break;
        case 3:
          dayCount += 30;
          break;
        case 4:
          dayCount += 31;
          break;
        case 5:
          dayCount += 30;
          break;
        case 6:
          dayCount += 31;
          break;
        case 7:
          dayCount += 31;
          break;
        case 8:
          dayCount += 30;
          break;
        case 9:
          dayCount += 31;
          break;
        case 10:
          dayCount += 30;
          break;
        case 11:
          dayCount += 31;
          break;
      }
      month--;
    }

    return dayCount;
  }

  setDateParserService() {
    const injector = Injector.create({
      providers: [{provide: DateParserService, deps: []}]
    });

    this.dateParserService = injector.get(DateParserService);
  }
}

export class DateValidModel {
  ptLuxonDate?: PtLuxonDate;
  isValid: boolean;
}



// WEBPACK FOOTER //
// ./src/models/projects/date.models.ts